import { Box, Button } from "@mui/material";
import { GRID_CHECKBOX_SELECTION_COL_DEF, GridColDef } from "@mui/x-data-grid-pro";
import { DateFilterOperator, StatusFilterOperator, TextFilterOperator } from "components/shared/filters";
import { TaskStatusCell } from "components/tasks";
import { AddRepeatTasksDrawer } from "components/tasks/repeat-tasks";
import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { formatDate } from "utils/date-utils";
import { MouseEvent, KeyboardEvent } from "react";

type UseWorkflowTasksColumnsProps = {
    onRefresh?: () => void;
};

const useWorkflowTasksColumns = (props?: UseWorkflowTasksColumnsProps) => {
    const { onRefresh } = props || {};
    const navigate = useNavigate();

    const handleRefresh = useCallback(() => {
        if (onRefresh) {
            onRefresh();
        }
    }, [onRefresh]);

    const navigateToTask = (taskId: number, event?: MouseEvent | KeyboardEvent): void => {
        if (event) {
            event.preventDefault();
            event.stopPropagation();
        }
        navigate(`/tasks/${taskId}/overview`);
    };
    
    const columns: GridColDef[] = [
        {
            field: 'name', headerName: 'Task',
            headerClassName: 'header',
            sortable: true, hideSortIcons: false,
            resizable: true,
            hideable: false,
            filterable: true,
            minWidth: 250,
            flex: 1,
            filterOperators: TextFilterOperator,
            renderCell: (params) => {
                const name = params.row.name;
                const taskId = params.row.id;
                const hasAccess = params.row.userHasAccess;
                return (
                    <>
                        {hasAccess ? (
                            <Button 
                                onClick={(e) => navigateToTask(taskId, e)} 
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        navigateToTask(taskId, e);
                                    }
                                }}
                                variant="text"
                                sx={{ 
                                    color: '#0066CC', 
                                    cursor: 'pointer', 
                                    textDecoration: 'underline',
                                    padding: '0',
                                    margin: '0',
                                    minWidth: 'unset',
                                    textTransform: 'none',
                                    fontWeight: 'normal',
                                    '&:focus': {
                                        outline: '2px solid #0066CC',
                                        outlineOffset: '2px'
                                    }
                                }} 
                                aria-label={`View task: ${name}`}
                                tabIndex={0}
                            >
                                {name}
                            </Button>
                        ) : name}
                        <AddRepeatTasksDrawer task={params.row} onTaskAdded={handleRefresh} />
                    </>
                )
            }
        },
        {
            field: 'status', headerName: 'Status',
            headerClassName: 'header',
            minWidth: 120, width: 200, sortable: true, resizable: true, hideable: false, hideSortIcons: false, filterOperators: StatusFilterOperator,
            renderCell: (params) => {
                return (<TaskStatusCell status={params.value} />)
            }
        },
        {
            field: 'startDate', headerName: 'Start Date',
            headerClassName: 'header',
            hideable: false,
            filterOperators: DateFilterOperator,
            sortable: true, hideSortIcons: false,
            resizable: true, width: 250,
            renderCell: (params) => {
                return (<div>{formatDate(params.value)}</div>)
            }
        },
        {
            field: 'endDate', headerName: 'End Date',
            headerClassName: 'header',
            sortable: true, hideSortIcons: false,
            hideable: false,
            filterOperators: DateFilterOperator,
            resizable: true, width: 250,
            renderCell: (params) => {
                const progress = params.row.progress;
                const formattedDate = formatDate(params.value);
                if (progress !== 'on-schedule') {
                    return (
                        <Box 
                            sx={{ color: '#EB0000', textDecoration: 'underline', textDecorationStyle: 'dashed', textDecorationThickness: '2px' }}
                            aria-label={`End date: ${formattedDate}, task is not on schedule`}
                            role="status"
                        >
                            {formattedDate}
                        </Box>
                    )
                } else {
                    return (
                        <div aria-label={`End date: ${formattedDate}`}>{formattedDate}</div>
                    )
                }
            }
        },
        {
            field: 'responsibility', headerName: 'Responsibility',
            headerClassName: 'header',
            hideable: false,
            sortable: true, hideSortIcons: false,
            resizable: true, width: 250,
            filterOperators: TextFilterOperator
        },
        {
            field: 'userName', headerName: 'Resource',
            headerClassName: 'header',
            sortable: true, hideSortIcons: false,
            resizable: true, width: 250,
            hideable: false,
            filterOperators: TextFilterOperator
        }
    ];

    return columns;
};

export default useWorkflowTasksColumns;